import { useMemo, useState, useCallback } from 'react';
import type { SimpleCondition, NotCondition } from '../../../types/policy';
import type { UseConditionReturn } from '../types';

/**
 * Custom hook for managing condition state and operations
 * 
 * This hook provides a comprehensive way to manage condition logic including:
 * - Handling NOT operations
 * - Managing operators and values
 * - Error handling
 * - Loading states
 * - Safe updates with error boundaries
 * 
 * @param condition - The current condition object (either SimpleCondition or NotCondition)
 * @param onUpdate - Callback function to handle condition updates
 * @returns UseConditionReturn object with condition state and handlers
 * 
 * @example
 * const {
 *   isNot,
 *   operator,
 *   value,
 *   handleOperatorChange,
 *   handleNotToggle
 * } = useCondition(currentCondition, updateCondition);
 */
export const useCondition = (
  condition: SimpleCondition | NotCondition,
  onUpdate: (condition: SimpleCondition | NotCondition) => void
): UseConditionReturn => {
  // State management for errors and loading
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handles errors that occur during condition operations
   */
  const handleError = useCallback((operation: string, error: Error) => {
    console.error(`Error in ${operation}:`, error);
    setError(error);
  }, []);

  /**
   * Clears any existing errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Safely executes update operations with error handling
   */
  const safeUpdate = useCallback(async (operation: string, updateFn: () => void) => {
    try {
      setIsLoading(true);
      clearError();
      await updateFn();
    } catch (err) {
      handleError(operation, err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [clearError, handleError]);

  // Determine if condition is negated and extract simple condition
  const isNot = 'not' in condition;
  const simpleCondition: SimpleCondition = isNot 
    ? (condition as NotCondition).not as SimpleCondition 
    : condition as SimpleCondition;
  
  const isValueCondition = useMemo(() => 'value' in simpleCondition, [simpleCondition]);

  const fieldOrValue = useMemo(() => 
    isValueCondition ? simpleCondition.value : simpleCondition.field, 
    [isValueCondition, simpleCondition]
  );

  // Extract operator from condition
  const operator = useMemo(() => 
    Object.keys(simpleCondition).find(key => key !== 'field' && key !== 'value') || 'equals',
    [simpleCondition]
  );
  
  const value = useMemo(() => {
    const currentValue = simpleCondition[operator];
    if (currentValue === undefined || currentValue === null) {
      return '';
    }
    if ((operator === 'in' || operator === 'notIn') && Array.isArray(currentValue)) {
      return JSON.stringify(currentValue);
    }
    return String(currentValue);
  }, [simpleCondition, operator]);

  /**
   * Handles changes to the condition operator
   */
  const handleOperatorChange = useCallback((newOperator: string) => {
    safeUpdate('operator change', () => {
      const { [operator]: oldOperatorValue, ...rest } = simpleCondition;
      
      // Reconstruct the condition, preserving either 'field' or 'value'
      const newCondition: SimpleCondition = { ...rest, [newOperator]: oldOperatorValue };
  
      onUpdate(isNot ? { not: newCondition } : newCondition);
    });
  }, [operator, simpleCondition, isNot, onUpdate, safeUpdate]);

  /**
   * Handles toggling the NOT operator
   */
  const handleNotToggle = useCallback((checked: boolean) => {
    safeUpdate('NOT toggle', () => {
      if (checked) {
        onUpdate({ not: simpleCondition });
      } else {
        onUpdate(simpleCondition);
      }
    });
  }, [simpleCondition, onUpdate, safeUpdate]);

  /**
   * Handles changes to the condition field
   */
  const handleFieldChange = useCallback((newFieldValue: string) => {
    safeUpdate('field change', () => {
      // Ensure we only store the alias (part before the parenthesised property path)
      const cleanedField = typeof newFieldValue === 'string' ? newFieldValue.split(' (')[0].trim() : newFieldValue;
      const newCondition: SimpleCondition = { ...simpleCondition };
      if (isValueCondition) {
        newCondition.value = cleanedField;
      } else {
        newCondition.field = cleanedField;
      }
      onUpdate(isNot ? { not: newCondition } : newCondition);
    });
  }, [simpleCondition, isValueCondition, isNot, onUpdate, safeUpdate]);

  /**
   * Handles changes to the condition value
   */
  const handleValueChange = useCallback((newValue: string) => {
    safeUpdate('value change', () => {
      let processedValue: string | string[];
      
      // Process array values for 'in' and 'notIn' operators
      if (operator === 'in' || operator === 'notIn') {
        try {
          // Try to parse as JSON array first
          processedValue = JSON.parse(newValue);
          if (!Array.isArray(processedValue)) {
            // If valid JSON but not array, treat as regular string
            processedValue = newValue;
          }
        } catch {
          // If not valid JSON, treat as regular string
          processedValue = newValue;
        }
      } else {
        processedValue = newValue;
      }

      const newCondition: SimpleCondition = { 
        ...simpleCondition, 
        [operator]: processedValue 
      };
      
      onUpdate(isNot ? { not: newCondition } : newCondition);
    });
  }, [simpleCondition, operator, isNot, onUpdate, safeUpdate]);

  const handleValueConditionToggle = useCallback(() => {
    safeUpdate('value condition toggle', () => {
      // Create a new object to hold the transformed condition
      const newCondition: SimpleCondition = {};
      
      // Transfer the main property ('field' or 'value')
      if (isValueCondition) {
        // Toggle from 'value' to 'field'
        newCondition.field = simpleCondition.value;
      } else {
        // Toggle from 'field' to 'value'
        newCondition.value = simpleCondition.field;
      }

      // Copy all other properties (operators and their values)
      for (const key in simpleCondition) {
        if (Object.prototype.hasOwnProperty.call(simpleCondition, key) && key !== 'field' && key !== 'value') {
          newCondition[key] = simpleCondition[key];
        }
      }

      onUpdate(isNot ? { not: newCondition } : newCondition);
    });
  }, [simpleCondition, isValueCondition, isNot, onUpdate, safeUpdate]);

  return {
    isNot,
    simpleCondition,
    isValueCondition,
    fieldOrValue: fieldOrValue || '',
    operator,
    value,
    error,
    isLoading,
    handleOperatorChange,
    handleNotToggle,
    handleFieldChange,
    handleValueChange,
    handleValueConditionToggle,
    clearError
  };
}; 